import React, { useEffect, useRef, useState } from 'react';
import Message from './Message';
import TypingIndicator from './TypingIndicator';
import APIProviderPill from './APIProviderPill';
import { MessageListProps } from './types';
import { getRandomProspectQuestions } from '../../lib/prospectQuestions';

const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  isLoading, 
  currentAIProvider = 'groq',
  lastFallbackReason 
}) => {
  // 🔍 DEBUG: Log MessageList props
  console.log('🔍 [MessageList] Received props:');
  console.log('🔍 [MessageList] currentAIProvider:', currentAIProvider === 'groq' ? 'G' : 'O');
  console.log('🔍 [MessageList] lastFallbackReason:', lastFallbackReason);
  console.log('🔍 [MessageList] messages.length:', messages.length);
  console.log('🔍 [MessageList] isLoading:', isLoading);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [randomQuestions, setRandomQuestions] = useState<string[]>([]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Set random questions on component mount
  useEffect(() => {
    setRandomQuestions(getRandomProspectQuestions(2));
  }, []);

  // Handle clicking on example questions
  const handleExampleQuestionClick = (question: string) => {
    const event = new CustomEvent('blp-question-click', { 
      detail: { question } 
    });
    window.dispatchEvent(event);
  };

  return (
    <div 
      ref={containerRef}
      className="h-full overflow-y-auto overscroll-contain px-4 py-6 relative"
    >
      {/* API Provider Indicator - Fixed position in top-right */}
      {messages.length > 0 && (
        <div className="fixed top-4 right-4 z-10">
          <APIProviderPill 
            provider={currentAIProvider}
            fallbackReason={lastFallbackReason}
          />
        </div>
      )}
      
      <div className="max-w-4xl mx-auto">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[60vh]">
            <div className="text-center text-light-secondary max-w-lg">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary-500/20 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 bg-primary-500 rounded-full animate-pulse-glow"></div>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-light-primary">BLP Sales Assistant</h3>
              <p className="text-base leading-relaxed mb-4">
                Ask me anything about the Beneficiary Liquidity Plan. I'll provide you with accurate, 
                sales-ready responses you can share with prospects during your calls.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 text-sm">
                {randomQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleQuestionClick(question)}
                    className="p-3 bg-dark-tertiary/30 rounded-lg border border-primary-500/20 
                             hover:border-primary-500/40 hover:bg-dark-tertiary/40 
                             transition-all duration-200 text-left group cursor-pointer"
                  >
                    <div className="font-medium text-primary-300 mb-1">
                      {index === 0 ? 'Try asking:' : 'Or:'}
                    </div>
                    <div className="text-light-tertiary group-hover:text-light-secondary transition-colors">
                      "{question}"
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-6 text-xs text-light-tertiary">
                RAG-Powered • BLP Knowledge Base • Real-time Assistance
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8 pb-6">
            {messages.map((message, index) => (
              <Message 
                key={message.id} 
                message={message} 
                isLatest={index === messages.length - 1}
                showAvatar={true}
              />
            ))}
            <TypingIndicator visible={isLoading} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageList;